import { createContext } from 'preact'
import { useCallback, useState, useEffect, useRef, useContext } from 'preact/hooks'
import { parseFilterList } from '../components/FilterUtils'
import { isAbortError } from '../athos-errors.mjs'

const initValue = {
  isLoading: false,
  error: null,
  results: [],
  options: new Map(),
  params: {},
  refine: () => {},
  clear: () => {}
}

const AthosContext = createContext(initValue)

function parseOptionFromURL(params, key) {
  const value = params.get(key)
  if (!value) return null

  switch (key) {
    case 'filters': {
      const filters = parseFilterList(value)
      return filters.length > 0 ? new Set(filters) : null
    }
    case 'page': {
      const pageNum = parseInt(value)
      return isNaN(pageNum) ? null : pageNum
    }
    default:
      return value
  }
}

function getInitialOptionsFromURL() {
  if (typeof window === 'undefined') return {}
  const params = new URLSearchParams(window.location.search)
  const initialOptions = {}

  for (const key of params.keys()) {
    const parsed = parseOptionFromURL(params, key)
    if (parsed !== null) initialOptions[key] = parsed
  }

  return initialOptions
}

export function useAthosFetcher(
  fetcher,
  initialiser = {
    options: {},
    runOnMount: true
  }
) {
  const { options: _options = {}, onFetch, runOnMount = true } = initialiser
  const isMounted = useRef(runOnMount)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)

  const urlOptions = useRef(getInitialOptionsFromURL())
  const mergedOptions = useRef({ ...urlOptions.current, ..._options })
  const [options, setOptions] = useState(new Map(Object.entries(mergedOptions.current)))
  const [results, setResults] = useState({})
  const params = fetcher?.params

  const refine = useCallback((key, value) => {
    setOptions((prev) => {
      const currentValue = prev.get(key)
      const isEmptySet = value instanceof Set && value.size === 0
      const isCleared = value === '' || value === null || value === undefined || isEmptySet

      if (isCleared) {
        if (key === 'filters') {
          if (currentValue instanceof Set && currentValue.size === 0) return prev
          const next = new Map(prev)
          next.set(key, new Set())
          return next
        }
        if (!prev.has(key)) return prev
        const next = new Map(prev)
        next.delete(key)
        return next
      }

      if (value instanceof Set && currentValue instanceof Set) {
        if (value.size === currentValue.size && [...value].every((item) => currentValue.has(item))) {
          return prev
        }
      } else if (value === currentValue) {
        return prev
      }

      const next = new Map(prev)
      next.set(key, value)
      return next
    })
  }, [])

  const clear = useCallback((key) => {
    if (!key) return
    setOptions((prev) => {
      const next = new Map(prev)
      next.delete(key)
      return next
    })
  }, [])

  useEffect(() => {
    function handleResultUpdate() {
      if (!isMounted.current) {
        isMounted.current = true
        return
      }
      setIsLoading(true)
      handleFetch()
    }

    const controller = new AbortController()
    handleResultUpdate()

    async function handleFetch() {
      try {
        fetcher.updateParams(Object.fromEntries(options.entries()))
        const data = await fetcher.fetch(controller)

        if (controller.signal.aborted || !data) return

        if (onFetch) {
          const normalisedResults = await onFetch(data, options)
          if (controller.signal.aborted) return
          setResults(normalisedResults)
        } else {
          setResults(data)
        }
        setError(null)
        setIsLoading(false)
      } catch (requestError) {
        // A replaced request is noise. Anything else is reported: without this an Athos
        // outage renders as an empty collection, indistinguishable from one with no products.
        if (isAbortError(requestError, controller.signal)) return
        console.error('[Athos] request failed', requestError)
        setError(requestError)
        setIsLoading(false)
      }
    }
    return () => {
      if (!controller.signal.aborted) controller.abort('replaced')
    }
  }, [options])

  return {
    isLoading,
    error,
    results,
    options,
    params,
    setOptions,
    refine,
    clear
  }
}

export function AthosProvider({ fetcher, onFetch, children, options, runOnMount }) {
  const value = useAthosFetcher(fetcher, {
    onFetch,
    options,
    runOnMount
  })

  return <AthosContext.Provider value={value}>{children}</AthosContext.Provider>
}

export function useAthosContext() {
  return useContext(AthosContext)
}

export function generateAthosProvider(FetcherClass, onFetch) {
  return function ({ children, category, runOnMount, ...options }) {
    const fetcherRef = useRef(new FetcherClass(category))
    return (
      <AthosProvider fetcher={fetcherRef.current} onFetch={onFetch} runOnMount={runOnMount} options={options}>
        {children}
      </AthosProvider>
    )
  }
}
